<template>
  <div class="dashboard-view">
    <div
      class="header flex flex-row justify-content-between align-items-center w-full mb-4"
    >
      <div
        class="flex flex-column justify-content-center align-items-start column-gap-1"
      >
        <h2 class="dashboard-main-title">
          Dashboard Overview
          <i class="fa-solid fa-chart-pie" style="color: #2563eb"></i>
        </h2>
      </div>
    </div>
    <!-- الفلتر العام -->
    <div
      class="dashboard-filter-row"
      style="display: flex; gap: 1rem; align-items: center; margin-bottom: 2rem"
    >
      <CorporateDropdown
        v-if="showCorporateDropdown"
        label="Select Corporate"
        v-model="selectedCorporateId"
        :usePlaceholder="true"
        containerClass="w-full md:w-3"
        dropdownStyle="width: 200px"
      />
      <Dropdown
        v-model="filterType"
        :options="filterOptions"
        optionLabel="label"
        optionValue="value"
        class="filter-dropdown"
      />
      <Calendar
        v-if="filterType === 'day'"
        v-model="filterDay"
        dateFormat="yy-mm-dd"
        placeholder="Select day"
        class="filter-calendar"
        showIcon
      />
      <template v-else>
        <Calendar
          v-model="filterFrom"
          dateFormat="yy-mm-dd"
          placeholder="From"
          class="filter-calendar"
          showIcon
        />
        <Calendar
          v-model="filterTo"
          dateFormat="yy-mm-dd"
          placeholder="To"
          class="filter-calendar"
          showIcon
        />
      </template>
    </div>
    <template v-if="shouldRender">
      <DashboardCards :filter="filterObj" />
      <div class="dashboard-bottom-row">
        <BookingSourcePieChart :filter="filterObj" />
        <DailyBookingBarChart :filter="filterObj" />
        <MonthlyRidePieChart :filter="filterObj" />
      </div>
      <RouteStatsTable :filter="filterObj" />
    </template>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import { useStore } from "vuex";
import DashboardCards from "@/components/dashboard/DashboardCards.vue";
import BookingSourcePieChart from "@/components/dashboard/BookingSourcePieChart.vue";
import DailyBookingBarChart from "@/components/dashboard/DailyBookingBarChart.vue";
import MonthlyRidePieChart from "@/components/dashboard/MonthlyRidePieChart.vue";
import RouteStatsTable from "@/components/dashboard/RouteStatsTable.vue";
import CorporateDropdown from "@/components/corporates/CorporateDropdown.vue";
import Dropdown from "primevue/dropdown";
import Calendar from "primevue/calendar";
import { getCurrentInstance } from "vue";

const { proxy } = getCurrentInstance();
const store = useStore();
const today = new Date();
const todayString = today.toISOString().slice(0, 10);
const filterType = ref("day");
const filterDay = ref(todayString);
const filterFrom = ref(todayString);
const filterTo = ref(null);
const selectedCorporateId = ref(null);

const showCorporateDropdown = computed(() => {
  try {
    const id = proxy.$getCorporateIdFromToken && proxy.$getCorporateIdFromToken();
    return !id;
  } catch (error) {
    console.warn('Error getting corporate ID from token:', error);
    return true; 
  }
});

const shouldRender = computed(() => {
  return !showCorporateDropdown.value || selectedCorporateId.value;
});

const filterOptions = [
  { label: "Day", value: "day" },
  { label: "Period", value: "period" },
];
const filterObj = computed(() => ({
  type: filterType.value,
  day: filterDay.value,
  from: filterFrom.value,
  to: filterTo.value,
  corporateId: selectedCorporateId.value,
}));

onMounted(() => {
  // سيتم جلب البيانات بناءً على الفلتر في الأبناء
});
</script>

<style scoped>
.dashboard-main-title {
  font-size: 2.2rem;
  font-weight: 800;
  color: #014b8f;
  margin-bottom: 0;
  letter-spacing: 1px;
  text-align: left;
  display: flex;
  align-items: center;
  gap: 0.7rem;
}
.mb-4 {
  margin-bottom: 1.5rem;
}
.dashboard-cards-flat {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 2rem;
  margin-top: 2rem;
}
.dashboard-card-flat {
  border-radius: 0.7rem;
  min-height: 100px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: box-shadow 0.15s, transform 0.15s;
  box-shadow: 0 2px 8px 0 rgba(44, 62, 80, 0.07);
  padding: 0.4rem 1.2rem 0.4rem 1.2rem;
}
.dashboard-card-flat-content {
  display: flex;
  align-items: center;
  gap: 1.2rem;
  width: 100%;
  justify-content: flex-start;
}
.dashboard-card-flat-center {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
}
.dashboard-card-flat-label,
.dashboard-card-flat-value {
  text-align: center;
  width: 100%;
}
.dashboard-card-flat-icon {
  font-size: 2.5rem;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
}
.dashboard-card-flat-label {
  color: #fff;
  font-size: 1.1rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
}
.dashboard-card-flat-value {
  color: #fff;
  font-size: 1.5rem;
  font-weight: 700;
}
@media (max-width: 900px) {
  .dashboard-cards-flat {
    grid-template-columns: repeat(2, 1fr);
    gap: 1rem;
  }
}
@media (max-width: 600px) {
  .dashboard-cards-flat {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
}
.dashboard-bottom-row {
  display: grid;
  grid-template-columns: 1fr 2fr 1fr;
  gap: 2rem;
  margin-top: 2.5rem;
  width: 100%;
}
.dashboard-pie-chart-container,
.dashboard-bar-chart-container {
  background: #fff;
  border-radius: 1rem;
  box-shadow: 0 2px 8px 0 rgba(44, 62, 80, 0.07);
  padding: 1.5rem 1.5rem 1rem 1.5rem;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  margin-bottom: 1.5rem;
  min-width: 0;
}
@media (max-width: 900px) {
  .dashboard-bottom-row {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
}
.no-chart-data {
  width: 100%;
  text-align: center;
  color: #888;
  font-size: 1.1rem;
  padding: 2.5rem 0;
}
.chart-title {
  font-size: 1.1rem;
  font-weight: 700;
  color: #2563eb;
  margin-bottom: 0.7rem;
  text-align: left;
  width: 100%;
  letter-spacing: 0.5px;
  padding-left: 0.2rem;
  align-self: flex-start;
}
.dashboard-route-stats-section {
  margin-top: 1.5em;
  margin-bottom: 2.5rem;
  background: #fff;
  border-radius: 1rem;
  box-shadow: 0 2px 8px 0 rgba(44, 62, 80, 0.07);
  padding: 1rem 1.5rem 1.5rem 1.5rem;
}
.route-stats-filter-row {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  align-items: center;
  margin-bottom: 1.5rem;
}
.filter-dropdown {
  min-width: 120px;
}
.filter-calendar {
  min-width: 140px;
}
.route-stats-table-wrapper {
  width: 100%;
  overflow-x: auto;
}
.route-stats-table {
  width: 100%;
  min-width: 800px;
}
:deep(.route-stats-table .p-datatable-thead > tr > th) {
  background-color: #014b8f !important;
  color: #fff !important;
  font-weight: 600 !important;
  border: none !important;
}
:deep(.route-stats-table .p-datatable-thead > tr > th:hover) {
  /* background-color: #013d77 !important; */
}

/* جدول الرحلات داخل expand */
:deep(.orange-header .p-datatable-thead > tr > th) {
  background-color: #fb923c !important;
  color: #fff !important;
  font-weight: 600 !important;
  border: none !important;
}
:deep(.orange-header .p-datatable-thead > tr > th:hover) {
  /* background-color: #ea7a1c !important; */
}
.no-route-stats {
  text-align: center;
  color: #888;
  font-size: 1.1rem;
  padding: 1.5rem 0;
}
.route-stats-title {
  font-size: 1.3rem;
  font-weight: 800;
  color: #014b8f;
  margin-bottom: 1rem;
  text-align: left;
  width: 100%;
  letter-spacing: 0.5px;
}
.route-rides-expansion {
  padding: 1rem;
  background-color: #f8f9fa;
  border-radius: 0.5rem;
  margin-top: 1rem;
}
.expansion-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}
.expansion-header h4 {
  margin-bottom: 0;
  color: #232946;
  font-size: 1.1rem;
  font-weight: 700;
}
.route-rides-table {
  width: 100%;
  min-width: 600px;
}
.route-rides-loading {
  width: 100%;
  min-height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.loading-content {
  width: 100%;
  text-align: center;
}
.loading-text {
  margin-top: 1rem;
  color: #6b7280;
  font-size: 0.9rem;
  font-weight: 500;
}
.no-route-rides {
  text-align: center;
  color: #888;
  font-size: 1rem;
  padding: 1rem 0;
}
.bookings-count {
  font-weight: 600;
  color: #2563eb;
}
.route-rides-table .p-datatable {
  font-size: 0.9rem;
}
.route-rides-table .p-datatable .p-datatable-thead > tr > th {
  background-color: #f8f9fa;
  font-weight: 600;
  color: #232946;
}
.route-rides-table .p-datatable .p-datatable-tbody > tr > td {
  padding: 0.5rem;
}
.rides-count {
  font-size: 0.9rem;
  color: #6b7280;
  font-weight: 400;
}
@media (max-width: 900px) {
  .dashboard-route-stats-section {
    padding: 1rem 0.5rem;
  }
  .route-stats-table {
    min-width: 600px;
  }
}
</style>
